// PhotoGallery — full-screen swipeable photo viewer shared by voice mode
// (VoicePanel) and the typed form chat. One image per page, native snap
// scrolling on touch, arrows on desktop, dots + counter. When micLive is
// set, a "still listening" chip reassures the customer the call keeps
// running underneath.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import brand from '../theme/brand';

/** Frosted-glass chip style shared by gallery/banner overlay controls. */
export const glassSx = {
  color: '#fff',
  backgroundColor: 'rgba(17,17,17,0.45)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.14)',
  '&:hover': { backgroundColor: 'rgba(17,17,17,0.65)' },
} as const;

/** Tiny 3-bar equalizer shown while a voice call is live. */
export const LiveBars: React.FC<{ color?: string }> = ({ color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 14 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 3,
          borderRadius: 2,
          backgroundColor: color || brand.sageLight,
          animation: `voiceEq 1.1s ease-in-out ${i * 0.18}s infinite`,
          '@keyframes voiceEq': {
            '0%, 100%': { height: 5 },
            '50%': { height: 14 },
          },
        }}
      />
    ))}
  </Box>
);

interface PhotoGalleryProps {
  open: boolean;
  urls: string[];
  /** Show the "still listening" chip while a voice call is running. */
  micLive?: boolean;
  onClose: () => void;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ open, urls, micLive = false, onClose }) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (open) setPage(0);
  }, [open]);

  const goTo = (i: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(urls.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
  };

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el || el.clientWidth === 0) return;
    setPage(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: '#000' } }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') goTo(page + 1);
        if (e.key === 'ArrowLeft') goTo(page - 1);
      }}
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        {/* Swipeable track — native touch momentum + snap */}
        <Box
          ref={scrollerRef}
          onScroll={handleScroll}
          sx={{
            display: 'flex',
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {urls.map((url, i) => (
            <Box
              key={`${url}-${i}`}
              sx={{
                flex: '0 0 100%',
                width: '100%',
                height: '100%',
                scrollSnapAlign: 'center',
                scrollSnapStop: 'always',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 1.5, sm: 3 },
                boxSizing: 'border-box',
              }}
            >
              <Box
                component="img"
                src={url}
                alt={`Photo ${i + 1}`}
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: 2,
                  userSelect: 'none',
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Top controls */}
        <IconButton
          onClick={onClose}
          aria-label="Close photos"
          sx={{ position: 'fixed', top: 12, right: 12, zIndex: 2, ...glassSx }}
        >
          <CloseIcon />
        </IconButton>
        {micLive && (
          <Box
            sx={{
              position: 'fixed',
              top: 16,
              left: 12,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              ...glassSx,
            }}
          >
            <LiveBars />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 500 }}>
              Still listening — keep talking
            </Typography>
          </Box>
        )}

        {urls.length > 1 && (
          <>
            {/* Desktop arrows */}
            {page > 0 && (
              <IconButton
                onClick={() => goTo(page - 1)}
                aria-label="Previous image"
                sx={{
                  position: 'fixed',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  display: { xs: 'none', sm: 'inline-flex' },
                  ...glassSx,
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
            {page < urls.length - 1 && (
              <IconButton
                onClick={() => goTo(page + 1)}
                aria-label="Next image"
                sx={{
                  position: 'fixed',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  display: { xs: 'none', sm: 'inline-flex' },
                  ...glassSx,
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}

            {/* Counter + dots */}
            <Box
              sx={{
                position: 'fixed',
                bottom: 20,
                left: 0,
                right: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                pointerEvents: 'none',
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {urls.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => goTo(i)}
                    sx={{
                      width: i === page ? 18 : 7,
                      height: 7,
                      borderRadius: 999,
                      backgroundColor:
                        i === page ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
                      transition: 'all 0.25s ease',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                    }}
                  />
                ))}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.75)',
                  px: 1.25,
                  py: 0.25,
                  borderRadius: 999,
                  backgroundColor: 'rgba(17,17,17,0.45)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {page + 1} of {urls.length}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default PhotoGallery;
